import { useState, useEffect } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import { Building, Plus, Pencil, Trash2, Loader2, Mail } from 'lucide-react';
import { sendWelcomeEmail } from '@/lib/email';
import { cn } from '@/lib/utils';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface Organization {
  id: string;
  name: string;
  admin_name: string | null;
  admin_phone: string | null;
  admin_email: string | null;
}

export function Organizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    admin_name: '',
    admin_phone: '',
    admin_email: ''
  });
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  async function loadOrganizations() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('*')
        .not('admin_email', 'like', '%@admin.com') // Exclude admin organizations
        .order('name');

      if (fetchError) {
        throw new Error(`Failed to load organizations: ${fetchError.message}`, { cause: fetchError });
      }

      setOrganizations(data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError(err instanceof Error ? err : new Error('Failed to load organizations'));
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingOrg) {
        const { error } = await supabase
          .from('organizations')
          .update(formData)
          .eq('id', editingOrg.id);

        if (error) throw error;
      } else {
        // Create new organization
        const { data: newOrg, error } = await supabase
          .from('organizations')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;

        // Send welcome email to admin
        try {
          await sendWelcomeEmail({
            adminName: formData.admin_name,
            adminEmail: formData.admin_email,
            orgName: formData.name,
            registerLink: `${window.location.origin}/register?email=${encodeURIComponent(formData.admin_email)}`
          });
        } catch (emailError) {
          console.error('Error sending welcome email:', emailError);
          // Don't throw here - we want to show success for org creation even if email fails
          setError(new Error('Organization created but failed to send welcome email. Please contact the admin manually.'));
        }
      }

      await loadOrganizations();
      setShowModal(false);
      setEditingOrg(null);
      setFormData({
        name: '',
        admin_name: '',
        admin_phone: '',
        admin_email: ''
      });
      setSuccess(editingOrg ? 'Organization updated successfully' : 'Organization created successfully');
    } catch (err) {
      console.error('Error saving organization:', err);
      setError(err instanceof Error ? err : new Error('Failed to save organization'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      admin_name: org.admin_name || '',
      admin_phone: org.admin_phone || '',
      admin_email: org.admin_email || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this organization?')) return;

    try {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadOrganizations();
      setSuccess('Organization deleted successfully');
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete organization'));
    }
  };

  const handleResendWelcome = async (org: Organization) => {
    try {
      setSendingEmail(org.id);
      setError(null);
      setSuccess(null);

      await sendWelcomeEmail({
        adminName: org.admin_name,
        adminEmail: org.admin_email,
        orgName: org.name,
        registerLink: `${window.location.origin}/register?email=${encodeURIComponent(org.admin_email)}`
      });

      setSuccess(`Welcome email resent to ${org.admin_email}`);
    } catch (err) {
      console.error('Error resending welcome email:', err);
      setError(new Error(`Failed to resend welcome email to ${org.admin_email}`));
    } finally {
      setSendingEmail(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Organizations</h1>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-5 w-5 mr-2" />
            Add Organization
          </Button>
        </div>

        {error && (
          <ErrorDisplay 
            error={error}
            context="Organization Management"
            onRetry={loadOrganizations}
            className="mb-4"
          />
        )}

        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organization
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Admin Contact
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {organizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{org.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      <div>{org.admin_name}</div>
                      <div>{org.admin_email}</div>
                      <div>{org.admin_phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendWelcome(org)}
                        disabled={sendingEmail === org.id}
                        title="Resend welcome email"
                      >
                        {sendingEmail === org.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(org)}
                        title="Edit organization"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(org.id)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete organization"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {editingOrg ? 'Edit Organization' : 'Add Organization'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Admin Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Admin Phone
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.admin_phone}
                    onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Admin Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setEditingOrg(null);
                      setFormData({
                        name: '',
                        admin_name: '',
                        admin_phone: '',
                        admin_email: ''
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}